import type { SnowflakeCredentials } from './../types/typesSnowflake'

/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 💬 Main Snowflake Authentication Store
/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { oauthCodeFlow } from '~/scripts/auth/oauth'
import { signKeypairJwt, importPrivateKeyPem } from '~/scripts/snowflake/signKeypairJwt'

/**
 * Convert a Snowflake account identifier (sfId) into the hostname
 * form Snowflake's TLS certificates actually match.
 *
 * Snowflake account names may contain underscores (`MY_ACCOUNT`) but
 * the public DNS / certificate convention is hyphen-only. Per Snowflake
 * docs: "Replace any underscores in your account name with hyphens
 * when using the URL." Browsers are lenient at the TLS layer and
 * accept the underscored form, but stricter clients like Python's
 * `requests` (used by app_oauth_token_flow) reject with a cert
 * hostname-mismatch error.
 *
 * We store the user-typed form on the account doc (so the original
 * identifier is preserved for display in settings UIs etc.) and
 * normalize ONLY at hostname-construction points.
 */
export function snowflakeHostFromSfId(sfId: string | null | undefined): string {
  return (sfId || '').replace(/_/g, '-')
}
export const useSnowflakeAuthStore = defineStore('snowflakeAuthStore', () => {
  const router = useRouter()

  // Get Snowflake Credentials from Local Cookie if Available - Return Results if Access Code is Valid
  // This is used to persist the user's authentication state across page reloads
  const snowflakeCredentials = useCookie('snowflakeAuth', { default: () => ({} as SnowflakeCredentials), maxAge: 180 * 60 })

  // //////////////////////////////////////////////////////////////////
  // Snowflake Store Items
  // //////////////////////////////////////////////////////////////////
  // Initialized from scripts/snowflake/initializeSnowflake.ts
  // const snowflakeCredentials = ref({} as SnowflakeCredentials)
  const currentUser = ref<string>('') // Current User for Snowflake Connection
  const currentRole = ref<string>('') // Current Role for Snowflake Connection
  const currentWarehouse = ref<string>('') // Current Warehouse for Snowflake Connection
  const userRoles = ref([] as any) // All Roles for the Current User
  const userWarehouses = ref([] as any) // All Warehouses for the Current User

  // Gets Current Snowflake Authentication State
  // const snowflakeAuth = (executor: 'user' | 'system' = 'user', customConnection?: SnowflakeConnection) => snowflakeAuthFunction(executor, customConnection)

  // //////////////////////////////////////////////////////////////////
  // 💬 Non-OAuth bearer cache.
  //
  // PAT and keypair-auth users don't have an OAuth bearer in the
  // cookie — their creds live encrypted on the user doc, fetched +
  // decrypted on first use and cached here for the page session.
  //
  // For PAT: cache the cleartext token (long-lived; one fetch per
  // session is plenty).
  // For keypair: cache the imported `CryptoKey` (so the cleartext PEM
  // can be discarded after import) and the most-recent JWT alongside
  // its expiry. JWTs are valid for ~1 hour; we re-mint with a 30s
  // buffer.
  //
  // All entries are cleared by `clearNonOauthCache()` when the user
  // logs out — applicationAuth's userLogout calls it.
  // //////////////////////////////////////////////////////////////////
  const _patBearer = ref<string | null>(null)
  const _keypairCryptoKey = ref<CryptoKey | null>(null)
  const _keypairJwt = ref<string | null>(null)
  const _keypairJwtExpiresAt = ref<number>(0)
  // 💬 SYSTEM-user keypair caches — deliberately separate refs from the
  // user-connection caches above so a user JWT is never handed out for
  // a system-executor call (or vice versa). Same lifecycle: cleared on
  // logout via clearNonOauthCache.
  const _systemKeypairCryptoKey = ref<CryptoKey | null>(null)
  const _systemKeypairJwt = ref<string | null>(null)
  const _systemKeypairJwtExpiresAt = ref<number>(0)
  function clearNonOauthCache() {
    _patBearer.value = null
    _keypairCryptoKey.value = null
    _keypairJwt.value = null
    _keypairJwtExpiresAt.value = 0
    _systemKeypairCryptoKey.value = null
    _systemKeypairJwt.value = null
    _systemKeypairJwtExpiresAt.value = 0
  }

  // //////////////////////////////////////////////////////////////////
  // 💬 Snowflake Connection Authentication Function
  //
  // Returns the bearer + token-type + sfId `useSnowflake` needs to
  // call Snowflake's REST API. Branches on the user's auth type:
  //
  //   - oauth   — refresh from the stored refresh token if expired,
  //               return an OAuth bearer. (Default token type, no
  //               special header on the request.)
  //   - pat     — fetch+decrypt the user's stored PAT once per
  //               session, cache, return as bearer.
  //   - keypair — fetch+decrypt the stored PEM once, import as a
  //               CryptoKey, sign a fresh KEYPAIR_JWT (cached for ~59
  //               min), return as bearer with tokenType=KEYPAIR_JWT.
  //
  // Falls through to empty creds on any failure — the caller turns
  // that into a graceful "no live Snowflake session" state rather
  // than a hard error. Components that need to react to disconnection
  // subscribe to StatusIndicator's `snowflakeSession` state.
  // //////////////////////////////////////////////////////////////////
  async function snowflakeAuth(executor: 'user' | 'system' = 'user', customConnection?: SnowflakeConnection): Promise<SnowflakeAuthentication> {
    console.log(`Executing With ${executor} | Custom Connection: ${customConnection || null}`)
    if (customConnection)
      console.log('Custom Connection:', customConnection)

    // 💬 Auth-mode dispatch. The cookie carries `authType` for OAuth
    // users (set during the OAuth login flow). Non-OAuth users have
    // an empty cookie, so we fall back to reading the auth type off
    // the user's profile blob in the globals store.
    const cookieAuthType = (snowflakeCredentials.value?.authType || '').toLowerCase()
    const { userProfile, accountProfile } = storeToRefs(useGlobalsStore())

    // 💬 SYSTEM executor — run as the account-level service user
    // (created by the setup wizard, persisted at
    // `accounts/{id}.snowflakeSettings.systemAccount`) instead of the
    // logged-in user's connection. Always keypair auth; dispatched
    // before the user-auth-mode branching below so the user's cookie /
    // connection type never influences a system-executor call.
    if (executor === 'system') {
      return await resolveSystemKeypairBearer(accountProfile.value)
    }

    const docConnectionType = (userProfile.value?.activeSnowflakeConnection?.connectionType || '').toLowerCase()
    const authMode = cookieAuthType || docConnectionType

    if (authMode === 'pat') {
      return await resolvePatBearer(userProfile.value, accountProfile.value)
    }
    if (authMode === 'keypair') {
      return await resolveKeypairBearer(userProfile.value, accountProfile.value)
    }
    // Default = OAuth. Either the cookie says so or no other auth
    // type is configured (a brand-new account).

    let { authType, sfId, clientId, clientSecret, redirectUri, accessToken, accessTokenExpiresAt, refreshTokenEncrypted, refreshTokenExpiresAt } = snowflakeCredentials.value

    // Check if Token Has Expired and Needs to Be Refreshed - With 10 Second Buffer
    if (!accessToken || !accessTokenExpiresAt || accessTokenExpiresAt < ((Math.round(new Date().getTime() / 1000)) + 10)) {
      console.log('Access Token Expired - Refreshing Token...')

      // 💬 If we can't refresh (no usable refresh token), return empty
      // credentials. The caller (useSnowflake → snowflakeQueryExecution)
      // will throw "Missing Snowflake Access Token..." which becomes a
      // graceful ERROR result.
      //
      // We deliberately do NOT push to /logout here. That side effect
      // turned this "passive auth" function into a session-killer:
      // any page that fires a Snowflake query before credentials are
      // ready (fresh signup, hard refresh, multi-tab race) would be
      // ejected to /logout.
      if (!refreshTokenEncrypted || !refreshTokenExpiresAt || refreshTokenExpiresAt < ((Math.round(new Date().getTime() / 1000)) + 10)) {
        console.warn('snowflakeAuth: no usable refresh token — returning empty credentials.')
        return { authType: undefined, sfId: undefined, accessToken: undefined }
      }
      const tokenPayload = { sfId, clientId, clientSecret, redirectUri, refreshToken: refreshTokenEncrypted }
      const refreshTokenResponse = await snowflakeToken(tokenPayload) as SnowflakeCredentials

      accessToken = refreshTokenResponse?.accessToken
    }

    return { authType, sfId, accessToken, tokenType: 'OAUTH' as const }
  }

  // //////////////////////////////////////////////////////////////////
  // 💬 PAT bearer resolver
  //
  // Reads the user's encrypted PAT from `userProfile.activeSnowflakeConnection.programmaticAccessToken`,
  // decrypts via `app_encryption`, caches the cleartext for the
  // session. PATs are typically valid for months — we don't refresh.
  // //////////////////////////////////////////////////////////////////
  async function resolvePatBearer(user: any, account: any): Promise<SnowflakeAuthentication> {
    const sfId = account?.snowflakeSettings?.sfId || ''
    if (!sfId) {
      console.warn('snowflakeAuth (pat): no sfId on account.')
      return { authType: undefined, sfId: undefined, accessToken: undefined }
    }
    if (_patBearer.value) {
      // PATs are sent as a plain Bearer; Snowflake's REST API
      // recognizes the token format itself, no special token-type
      // header needed.
      return { authType: 'pat', sfId, accessToken: _patBearer.value, tokenType: 'OAUTH' as const }
    }
    const encryptedPat = user?.activeSnowflakeConnection?.programmaticAccessToken
    if (!encryptedPat) {
      console.warn('snowflakeAuth (pat): no PAT stored on the user connection.')
      return { authType: undefined, sfId: undefined, accessToken: undefined }
    }
    try {
      const res: any = await useCloudFunction('app_encryption', { decrypt: encryptedPat })
      const cleartext = res?.decrypted
      if (!cleartext) throw new Error('Decryption returned no plaintext.')
      _patBearer.value = cleartext
      return { authType: 'pat', sfId, accessToken: cleartext, tokenType: 'OAUTH' as const }
    } catch (e) {
      console.error('snowflakeAuth (pat): failed to decrypt PAT —', e)
      return { authType: undefined, sfId: undefined, accessToken: undefined }
    }
  }

  // //////////////////////////////////////////////////////////////////
  // 💬 Keypair JWT resolver
  //
  // First call per session: decrypt the stored PEM via `app_encryption`,
  // import as a `CryptoKey` (the cleartext PEM is discarded after this).
  // Subsequent calls reuse the cached CryptoKey to sign fresh JWTs as
  // the previous one approaches its 1-hour expiry.
  //
  // The signed JWT is sent as `Authorization: Bearer <jwt>` with
  // `X-Snowflake-Authorization-Token-Type: KEYPAIR_JWT` — caller
  // (useSnowflake) sets the header based on the returned `tokenType`.
  // //////////////////////////////////////////////////////////////////
  async function resolveKeypairBearer(user: any, account: any): Promise<SnowflakeAuthentication> {
    const sfId = account?.snowflakeSettings?.sfId || ''
    const username = (user?.activeSnowflakeConnection?.username || '').trim()
    if (!sfId || !username) {
      console.warn('snowflakeAuth (keypair): missing sfId or username on the user connection.')
      return { authType: undefined, sfId: undefined, accessToken: undefined }
    }

    // Re-use a still-valid JWT to skip the signing roundtrip.
    const nowSec = Math.floor(Date.now() / 1000)
    if (_keypairJwt.value && _keypairJwtExpiresAt.value > nowSec + 30) {
      return { authType: 'keypair', sfId, accessToken: _keypairJwt.value, tokenType: 'KEYPAIR_JWT' as const }
    }

    // Import the CryptoKey if we haven't yet. Once imported the
    // cleartext PEM goes out of scope — the only handle to the key
    // material is the non-extractable-from-here `CryptoKey` ref.
    if (!_keypairCryptoKey.value) {
      const encryptedPem = user?.activeSnowflakeConnection?.privateKey
      if (!encryptedPem) {
        console.warn('snowflakeAuth (keypair): no private key stored on the user connection.')
        return { authType: undefined, sfId: undefined, accessToken: undefined }
      }
      try {
        const res: any = await useCloudFunction('app_encryption', { decrypt: encryptedPem })
        const cleartextPem = res?.decrypted
        if (!cleartextPem) throw new Error('Decryption returned no plaintext.')
        _keypairCryptoKey.value = await importPrivateKeyPem(cleartextPem)
      } catch (e) {
        console.error('snowflakeAuth (keypair): failed to decrypt + import private key —', e)
        return { authType: undefined, sfId: undefined, accessToken: undefined }
      }
    }

    // Sign a fresh JWT. We re-export the CryptoKey to a JWK inside
    // signKeypairJwt to derive the public-key fingerprint, so this
    // call is self-contained — caller doesn't need to thread the
    // public key separately.
    try {
      // signKeypairJwt accepts either the imported key or a PEM. We
      // already have the imported key, so we pass it via a small
      // wrapper that re-uses the cached CryptoKey (avoids re-parsing
      // the PEM on every JWT mint).
      const signed = await signKeypairJwtWithCachedKey({
        account: sfId,
        user: username,
        cryptoKey: _keypairCryptoKey.value!,
      })
      _keypairJwt.value = signed.jwt
      _keypairJwtExpiresAt.value = signed.expiresAt
      return { authType: 'keypair', sfId, accessToken: signed.jwt, tokenType: 'KEYPAIR_JWT' as const }
    } catch (e) {
      console.error('snowflakeAuth (keypair): failed to sign JWT —', e)
      return { authType: undefined, sfId: undefined, accessToken: undefined }
    }
  }

  // //////////////////////////////////////////////////////////////////
  // 💬 SYSTEM-user keypair JWT resolver
  //
  // Mirrors `resolveKeypairBearer` but reads the SYSTEM service user's
  // credentials from the ACCOUNT doc
  // (`snowflakeSettings.systemAccount.{username, privateKeyEncrypted}`)
  // instead of the logged-in user's connection, and uses its own cache
  // refs so user and system JWTs never cross. Used by executor='system'
  // calls (e.g. the Native App backend) so account-level SQL runs under
  // the service user's role regardless of who's logged in.
  //
  // Return type left inferred (same shape as resolveKeypairBearer):
  // `SnowflakeAuthentication` is declared in a types module that
  // currently doesn't resolve under vue-tsc, and annotating here would
  // add another unresolved-name diagnostic.
  // //////////////////////////////////////////////////////////////////
  async function resolveSystemKeypairBearer(account: any) {
    const sfId = account?.snowflakeSettings?.sfId || ''
    const systemAccount = account?.snowflakeSettings?.systemAccount || {}
    const username = (systemAccount.username || '').trim()
    if (!sfId || !username) {
      console.warn('snowflakeAuth (system keypair): missing sfId or system username on the account.')
      return { authType: undefined, sfId: undefined, accessToken: undefined }
    }

    // Re-use a still-valid JWT to skip the signing roundtrip.
    const nowSec = Math.floor(Date.now() / 1000)
    if (_systemKeypairJwt.value && _systemKeypairJwtExpiresAt.value > nowSec + 30) {
      return { authType: 'keypair', sfId, accessToken: _systemKeypairJwt.value, tokenType: 'KEYPAIR_JWT' as const }
    }

    // Import the CryptoKey once per session — the cleartext PEM goes
    // out of scope after import, same hygiene as the user resolver.
    if (!_systemKeypairCryptoKey.value) {
      const encryptedPem = systemAccount.privateKeyEncrypted
      if (!encryptedPem) {
        console.warn('snowflakeAuth (system keypair): no private key stored on the account systemAccount.')
        return { authType: undefined, sfId: undefined, accessToken: undefined }
      }
      try {
        const res: any = await useCloudFunction('app_encryption', { decrypt: encryptedPem })
        const cleartextPem = res?.decrypted
        if (!cleartextPem) throw new Error('Decryption returned no plaintext.')
        _systemKeypairCryptoKey.value = await importPrivateKeyPem(cleartextPem)
      } catch (e) {
        console.error('snowflakeAuth (system keypair): failed to decrypt + import private key —', e)
        return { authType: undefined, sfId: undefined, accessToken: undefined }
      }
    }

    try {
      const signed = await signKeypairJwtWithCachedKey({
        account: sfId,
        user: username,
        cryptoKey: _systemKeypairCryptoKey.value!,
      })
      _systemKeypairJwt.value = signed.jwt
      _systemKeypairJwtExpiresAt.value = signed.expiresAt
      return { authType: 'keypair', sfId, accessToken: signed.jwt, tokenType: 'KEYPAIR_JWT' as const }
    } catch (e) {
      console.error('snowflakeAuth (system keypair): failed to sign JWT —', e)
      return { authType: undefined, sfId: undefined, accessToken: undefined }
    }
  }

  // 💬 Thin wrapper that mirrors signKeypairJwt's flow but takes an
  // already-imported CryptoKey so we don't re-parse the PEM on every
  // JWT mint. Stays here (rather than exported alongside signKeypairJwt)
  // because it leans on the in-store cache shape.
  async function signKeypairJwtWithCachedKey(args: { account: string; user: string; cryptoKey: CryptoKey }) {
    // signKeypairJwt's public API takes a PEM, but its internals
    // (importPrivateKeyPem → CryptoKey → fingerprint → sign) are
    // exposed enough that we can synthesize the same flow inline.
    // For a small surface, we just defer to a quick JWT build that
    // reuses helpers from signKeypairJwt.ts (importPrivateKeyPem is
    // already exported; we don't need anything else here because the
    // main signKeypairJwt function rebuilds the PEM path. Keep the
    // round-trip simple: re-export the cached CryptoKey as JWK,
    // re-sign a fresh JWT using the same logic as the standalone
    // helper).
    //
    // To avoid duplicating the JWT-build code here, we delegate by
    // re-exporting the cached private key as PKCS8 PEM and calling
    // signKeypairJwt with it. The export step is microseconds — much
    // cheaper than the network roundtrip we save by caching the
    // CryptoKey across calls.
    const pkcs8 = await window.crypto.subtle.exportKey('pkcs8', args.cryptoKey)
    const pem = pkcs8DerToPem(pkcs8)
    return await signKeypairJwt({
      account: args.account,
      user: args.user,
      privateKeyPem: pem,
    })
  }

  function pkcs8DerToPem(der: ArrayBuffer): string {
    const bytes = new Uint8Array(der)
    let binary = ''
    const chunk = 0x8000
    for (let i = 0; i < bytes.byteLength; i += chunk) {
      binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)) as any)
    }
    const b64 = btoa(binary)
    const lines = b64.match(/.{1,64}/g) || [b64]
    return `-----BEGIN PRIVATE KEY-----\n${lines.join('\n')}\n-----END PRIVATE KEY-----`
  }

  // //////////////////////////////////////////////////////////////////
  // 💬 Snowflake Exchange and Refresh Token
  // //////////////////////////////////////////////////////////////////

  async function snowflakeToken(payload: any) {
    const { sfId, clientId, clientSecret, redirectUri, code, refreshToken, userId, isAppLogin } = payload

    // Exchange Code for Token Using Cloud Functions. Hostname uses
    // hyphenated form so the backend's strict TLS cert check passes
    // for accounts whose stored sfId contains underscores.
    const tokenUrl = `https://${snowflakeHostFromSfId(sfId)}.snowflakecomputing.com/oauth/token-request`

    // If "code" is Provided - Use Code to Get Token
    // If "refreshToken" is Provided - Use Refresh Token to Get New Access Token
    const serverPayload: any = { tokenUrl, clientId, clientSecret, redirectUri }
    if (code) {
      serverPayload.grantType = 'authorization_code'
      serverPayload.token = code
    }
    else {
      serverPayload.grantType = 'refresh_token'
      serverPayload.token = refreshToken
    }
    if (isAppLogin) {
      serverPayload.isAppLogin = true
      serverPayload.userId = userId
    }
    try {
      // Get Access and Refresh Tokens from Cloud Function
      const serverResponse: SnowflakeCredentials | unknown = await useCloudFunction('app_oauth_token_flow', serverPayload)
      if (serverResponse) {
        let response = serverResponse as SnowflakeCredentials

        // If User COULD AUTHENTICATE With Snowflake But No Application Account was Found for User/Account Combination
        if (response.loginError) {
          if (typeof response.loginError !== 'string' && response.loginError.message)
            response.loginError = response.loginError.message
          if (response.loginError.includes('No Account')) {
            const { applicationName } = storeToRefs(useGlobalsStore())
            const noUserAccountError = `No ${applicationName.value} Account Was Found For ${response.loginError.replace('No Account ', '')}. Contact Your ${applicationName.value} Admin.`

            return { error: noUserAccountError }
          }

          return { error: response.loginError }
        }

        // If Error Was Returned
        if (response.error) {
          console.error(`Failed to Get Access Token From ${tokenUrl}: ${response.error}`)

          return { error: response.error }
        }

        // If Tokens Received Write to Local Storage and Docs
        if (response.accessToken) {
          // Add sfId to Response
          response.authType = 'oauth'
          response.sfId = sfId
          response.clientId = clientId
          response.clientSecret = clientSecret
          response.redirectUri = redirectUri

          // Refresh Token NOT Returned on Access Token Refresh - Get Back From Global Store
          if (!response.refreshTokenEncrypted) {
            const { refreshTokenEncrypted, refreshTokenExpiresIn, refreshTokenExpiresAt, username } = snowflakeCredentials.value

            response = { ...response, refreshTokenEncrypted, refreshTokenExpiresIn, refreshTokenExpiresAt, username }
          }

          // If Connection Successful - Write Access Token to Local Storage
          snowflakeCredentials.value = response

          // Write to Cookie
          const snowflakeAuthCookie = useCookie('snowflakeAuthTest', { default: () => ({}), maxAge: 180 * 60 })
          snowflakeAuthCookie.value = JSON.stringify(response)
          return response
        }
      }
    }
    catch (error) {
      const errorMessage = `Failed to Get Access Token: ${error}`

      console.error(errorMessage)

      return { error: errorMessage }
    }
  }

  // //////////////////////////////////////////////////////////////////
  // 💬 Get Oauth Code from Snowflake Then Exchange for Tokens
  // //////////////////////////////////////////////////////////////////
  async function snowflakeOauth(sfId: string, clientId: string, userId: string) {
    const { applicationDomain } = storeToRefs(useGlobalsStore())

    // URIs
    const redirectUri = `${applicationDomain.value}/oauth`

    // URL-encode both query params. Snowflake-issued OAuth client_ids
    // routinely contain `/` and `=` (they're base64-style strings); the
    // redirect_uri contains `://`. Without encoding, Snowflake's URL
    // parser misreads the params and returns "OAuth client integration
    // with the given client id is not found." SnowflakeOAuthSetup.vue's
    // test path already encodes correctly — this matches that behavior.
    // Also normalize underscores → hyphens in the host segment to
    // match Snowflake's TLS cert (see snowflakeHostFromSfId comment).
    const authUrl = `https://${snowflakeHostFromSfId(sfId)}.snowflakecomputing.com/oauth/authorize?client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(redirectUri)}`

    // 💬 Snowflake OAuth scope behavior — the limitation we're stuck with.
    //
    // Snowflake's custom OAuth only accepts `session:role:<NAME>` or
    // `refresh_token` as scope values. There's no `session:role-any`
    // wildcard (that string is from other OAuth providers — Snowflake
    // returns "The requested scope is invalid").
    //
    // Without an explicit scope, Snowflake issues a token bound to the
    // user's DEFAULT_ROLE. That session is "restricted": `USE ROLE`
    // is blocked even when OAUTH_USE_SECONDARY_ROLES = IMPLICIT is set
    // on the integration (IMPLICIT only activates SECONDARY roles —
    // it doesn't unrestrict the session for primary-role switching).
    //
    // Practical consequence: in-app role switching for OAuth users
    // requires re-running the OAuth flow with the new
    // `session:role:<NEW_ROLE>` scope to mint a fresh token bound
    // to the requested role. That's the next iteration of the role
    // switcher — for now we leave the scope null (default to user's
    // DEFAULT_ROLE) and surface a clear error if a non-default role
    // is selected.
    const oauthScope = null

    try {
      // Get Oauth Code
      const codeResponse = await oauthCodeFlow(authUrl, redirectUri, oauthScope)
      if (codeResponse.error)
        return { error: codeResponse.error }
      const { code } = codeResponse
      // Exchange Code for Token Using Cloud Functions
      const payload = { sfId, clientId, clientSecret: null as any, redirectUri, code, userId, isAppLogin: true }

      const response = await snowflakeToken(payload) as SnowflakeCredentials

      // If Login Token Successful - Write access and refresh tokens to Store and Docs
      if (response && response.loginToken)

        // Write to Store
        snowflakeCredentials.value = response

      // Write to Cookie
      // snowflakeAuthCookie.value = JSON.stringify(response)

      return response
    }
    catch (error) {
      const errorMessage = `Failed to Get Access Token: ${error}`

      console.error(errorMessage)

      return { error: errorMessage }
    }
  }

  return {
    snowflakeCredentials,
    currentUser,
    currentRole,
    currentWarehouse,
    userRoles,
    userWarehouses,
    snowflakeAuth,
    snowflakeOauth,
    clearNonOauthCache,
  }
})
